// MP-OS-192 — the largest-brand line under a supplier's own share.
//
// The line used to print the segment's largest brand with its share whoever owned it. When that
// brand is the supplier's own (Swedish Match: ZYN, a single-brand All White supplier), the card
// showed the same figure twice — once as "your share", once as "the largest brand". So: when the
// leader is the supplier's own, the line says so without a figure and the largest COMPETITOR
// moves to the ⓘ; when the leader is a competitor, the line keeps it with its share.
//
// Ownership is the server's `own` tag on each ranking row (MPD-73: from the supplier's own
// articles, so it holds for any supplier and any trend filter). Only an older API without the tag
// falls back to the BrandPicker rule (register + the brand list's own tag, resolvedOwnBrands). A
// brand not tagged own — including one whose ownership is unknown — is a competitor.
//
// The largest competitor comes from the server too: it ranks the whole segment, while the rows
// here are cut at 12, so "no competitor brand" is only said when the server found none. With an
// older API the line only claims what the rows show ("none among the top N").
//
// Sales without an identified brand are one bucket in the ranking (resolved: false). It is never a
// leader or a competitor; its share stays in the segment total, and when it is larger than the
// top brand the line says "largest identified" and the ⓘ gives its share (MPD-68 review).
//
// Pure so the rule is unit-tested without rendering the card.

import { isOwn } from './brandSelection'
import { plural } from './format'

export interface BrandShare {
  brand: string
  share_of_segment: number
  resolved?: boolean // false = the bucket of sales without an identified brand
  own?: boolean // the server's own-brand tag; absent from an older API
}

// `unresolved` = the share of the segment with no identified brand (0 when none); `identifiedOnly`
// = that bucket is larger than the leader, so the leader is only the largest IDENTIFIED brand.
type Unresolved = { unresolved: number; identifiedOnly: boolean }
// `shownRows` = how many rows the ranking has when the competitor search only covered those rows
// (an older API); null = the server ranked the whole segment, so a null competitor means none.
export type LargestBrand =
  | ({ kind: 'own'; brand: string; competitor: BrandShare | null; shownRows: number | null } & Unresolved)
  | ({ kind: 'competitor'; brand: string; share: number } & Unresolved)

const byShare = (rows: readonly BrandShare[]) =>
  [...rows].sort((a, b) => b.share_of_segment - a.share_of_segment)

/** The segment's largest brand, classified against the supplier's own brands. null = no data.
 *  `serverCompetitor` is the API's largest competitor over the whole segment (null = none);
 *  undefined for an older API, which leaves the search to the rows shown. */
export function largestBrand(
  rows: readonly BrandShare[],
  ownBrands: readonly string[],
  serverCompetitor?: BrandShare | null,
): LargestBrand | null {
  const own = (r: BrandShare) => r.own ?? isOwn(r.brand, ownBrands)
  const ranked = byShare(rows).filter((r) => r.resolved !== false && r.brand.trim())
  const top = ranked[0]
  if (!top) return null
  const unresolved = Math.round(rows.filter((r) => r.resolved === false)
    .reduce((sum, r) => sum + r.share_of_segment, 0) * 10) / 10
  const u = { unresolved, identifiedOnly: unresolved > top.share_of_segment }
  if (!own(top)) return { kind: 'competitor', brand: top.brand, share: top.share_of_segment, ...u }
  if (serverCompetitor !== undefined) return { kind: 'own', brand: top.brand, competitor: serverCompetitor, shownRows: null, ...u }
  return { kind: 'own', brand: top.brand, competitor: ranked.find((r) => !own(r)) ?? null, shownRows: rows.length, ...u }
}

/**
 * The visible line and its ⓘ text. `period` is the month the brand ranking is for — the API only
 * ranks brands per month, so the line names that month itself and never silently borrows the
 * all-time period the share beside it may show. `pct` formats a share. `marketNote` narrows the
 * definition where the ranking is scoped to the supplier's product types (MPD-68: Vape, Smoking).
 */
export function largestBrandText(
  lb: LargestBrand,
  segment: string,
  period: string,
  pct: (n: number) => string,
  marketNote = '',
): { line: string; tip: string } {
  const basis = `Brand shares are each brand’s CU as a percentage of all ${segment} CU sold in the whole market in ${period}.${marketNote} `
    + 'Brand rankings are only available per month, so this line stays on the selected month even when the share above shows all time.'
    + (lb.unresolved > 0
      ? ` ${pct(lb.unresolved)} of ${segment} CU in ${period} has no identified brand; it is not ranked as a brand but stays in the total.`
      : '')
  const largest = lb.identifiedOnly ? 'largest identified' : 'largest'
  if (lb.kind === 'competitor') {
    return {
      line: `${lb.identifiedOnly ? 'Largest identified' : 'Largest'} ${segment} brand in ${period}: ${lb.brand}, ${pct(lb.share)}`,
      tip: `${lb.brand} is a competitor brand. ${basis}`,
    }
  }
  const competitor = lb.competitor
    ? `Largest competitor brand: ${lb.competitor.brand}, ${pct(lb.competitor.share_of_segment)} of ${segment} CU · ${period}.`
    : lb.shownRows != null
      ? `No competitor brand among the top ${lb.shownRows} ${segment} ${plural(lb.shownRows, 'brand')} in ${period}.`
      : `No competitor brand has ${segment} sales data for ${period}.`
  return {
    line: `Your brand ${lb.brand} is the ${largest} ${segment} brand in ${period}`,
    tip: `${competitor} ${basis}`,
  }
}
